import React, { useState } from 'react';
import { FiCheckCircle } from 'react-icons/fi';
import { API_URL } from '../config';

const SURVEY_TYPES = [
  'Boundary',
  'Topographic',
  'ALTA / NSPS',
  'Construction Stakeout',
  'As-Built',
  'Foundation',
  'Subdivision / Plat',
  'Elevation Certificate',
  'Mortgage Inspection',
  'Other / Not sure',
];

function PublicQuoteRequestForm() {
  const [form, setForm] = useState({
    first_name: '', last_name: '',
    phone: '', email: '',
    property_address: '', survey_type: '', project_details: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  const set = (field) => (e) => setForm((p) => ({ ...p, [field]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.first_name.trim() || !form.phone.trim() || !form.email.trim()) {
      setError('Please fill in your name, phone, and email.');
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/public/quote-request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (res.ok) {
        setDone(true);
      } else {
        setError(data.error || 'Something went wrong. Please try again.');
      }
    } catch {
      setError('Network error. Please try again.');
    }
    setSubmitting(false);
  };

  const inputStyle = {
    width: '100%', padding: '12px 14px', border: '1px solid #d1d5db',
    borderRadius: '8px', fontSize: '16px', boxSizing: 'border-box',
  };
  const labelStyle = {
    display: 'block', fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '5px',
  };

  if (done) {
    return (
      <div style={{ minHeight: '100vh', background: '#f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
        <div style={{ background: 'white', borderRadius: '16px', padding: '48px 32px', textAlign: 'center', maxWidth: '440px', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
          <FiCheckCircle size={56} color="#16a34a" />
          <h2 style={{ margin: '16px 0 8px', color: '#111827' }}>Thanks — request received!</h2>
          <p style={{ color: '#6b7280', margin: 0, lineHeight: 1.5 }}>
            We've got your information. Someone from North Georgia Surveying will reach out shortly with next steps and a quote.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: '#f3f4f6', padding: '24px 16px' }}>
      <div style={{ maxWidth: '560px', margin: '0 auto' }}>
        <div style={{ background: '#1e293b', color: 'white', borderRadius: '12px 12px 0 0', padding: '24px 28px', textAlign: 'center' }}>
          <h1 style={{ margin: 0, fontSize: '22px' }}>North Georgia Surveying, Inc.</h1>
          <p style={{ margin: '6px 0 0', opacity: 0.85, fontSize: '14px' }}>Request a Free Quote</p>
        </div>

        <form onSubmit={handleSubmit} style={{ background: 'white', borderRadius: '0 0 12px 12px', padding: '28px', boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}>
          <p style={{ fontSize: '14px', color: '#6b7280', margin: '0 0 20px', lineHeight: 1.5 }}>
            Fill in the form below and we'll get back to you with a quote.
            Fields marked <span style={{ color: '#dc2626' }}>*</span> are required.
          </p>

          <div style={{ marginBottom: '6px', fontSize: '12px', fontWeight: '700', color: '#1e40af', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Your Info</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '14px' }}>
            <div>
              <label style={labelStyle}>First Name <span style={{ color: '#dc2626' }}>*</span></label>
              <input style={inputStyle} value={form.first_name} onChange={set('first_name')} />
            </div>
            <div>
              <label style={labelStyle}>Last Name</label>
              <input style={inputStyle} value={form.last_name} onChange={set('last_name')} />
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '20px' }}>
            <div>
              <label style={labelStyle}>Phone <span style={{ color: '#dc2626' }}>*</span></label>
              <input style={inputStyle} type="tel" value={form.phone} onChange={set('phone')} placeholder="(706) 555-0123" />
            </div>
            <div>
              <label style={labelStyle}>Email <span style={{ color: '#dc2626' }}>*</span></label>
              <input style={inputStyle} type="email" value={form.email} onChange={set('email')} placeholder="you@example.com" />
            </div>
          </div>

          <div style={{ marginBottom: '6px', fontSize: '12px', fontWeight: '700', color: '#1e40af', textTransform: 'uppercase', letterSpacing: '0.5px' }}>About the Project</div>
          <div style={{ marginBottom: '14px' }}>
            <label style={labelStyle}>Type of Survey <span style={{ color: '#9ca3af', fontWeight: 400 }}>(optional)</span></label>
            <select style={inputStyle} value={form.survey_type} onChange={set('survey_type')}>
              <option value="">— Select a survey type —</option>
              {SURVEY_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div style={{ marginBottom: '14px' }}>
            <label style={labelStyle}>Property Address or Parcel Number <span style={{ color: '#9ca3af', fontWeight: 400 }}>(optional)</span></label>
            <input style={inputStyle} value={form.property_address} onChange={set('property_address')} placeholder="123 Main St, Calhoun GA" />
          </div>
          <div style={{ marginBottom: '20px' }}>
            <label style={labelStyle}>Project Details <span style={{ color: '#9ca3af', fontWeight: 400 }}>(optional)</span></label>
            <textarea
              style={{ ...inputStyle, fontFamily: 'inherit', resize: 'vertical' }}
              rows={4}
              value={form.project_details}
              onChange={set('project_details')}
              placeholder="Anything else we should know? Timeline, access notes, specific questions..."
            />
          </div>

          {error && (
            <div style={{ background: '#fee2e2', color: '#b91c1c', padding: '10px 14px', borderRadius: '8px', fontSize: '14px', marginBottom: '16px' }}>
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={submitting}
            style={{
              width: '100%', padding: '14px', background: submitting ? '#9ca3af' : '#16a34a',
              color: 'white', border: 'none', borderRadius: '8px', fontSize: '16px',
              fontWeight: '600', cursor: submitting ? 'wait' : 'pointer',
            }}
          >
            {submitting ? 'Submitting...' : 'Submit Quote Request'}
          </button>
        </form>
      </div>
    </div>
  );
}

export default PublicQuoteRequestForm;
